import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect, ConnectedProps } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ReplayIcon from "@mui/icons-material/Replay";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import PageHeader from "&styled/page-header";
import SelectComponent from "&styled/form/select";
import { getCurrentUser } from "&config/getCurrentUser";
import { colorPalette } from "&config/colors";
import { confirmAlert, infoAlert, successAlert } from "&config/swalGenerator";
import { WALEE_BASE_URL } from "&config/url";
import { RootState } from "&store/store";

type ReduxProps = ConnectedProps<typeof connector>;
type RouteParams = { jobId: string };
type ConsumerNumberRecord = {
    _id: string;
    serviceId: string;
    serviceName: string;
    consumerNumber: string;
    msisdn: string;
    fullName: string;
    customerEmail: string;
    amount: number;
    dueDate: string;
    product: string;
    status: string;
    createdAt: string;
};
type ConsumerNumberRecordsResponse = {
    data: Array<Record<string, any>>;
    current: number;
    perPage: number;
    pages: number;
    total: number;
};

const statusStyles = {
    pending: { background: "#FFF4D6", color: "#9B7400" },
    success: { background: "#E6F7EA", color: "#138A36" },
    failed: { background: "#FDEAEA", color: "#C62828" },
};

const ConsumerNumberRequestRecordsComponent = (props: ReduxProps) => {
    const { token } = props;
    const { jobId } = useParams<RouteParams>();
    const history = useHistory();
    const currentUser = getCurrentUser(token) || { role: { permissions: {} } };
    const canView = !!currentUser.role?.permissions?.ConsumerNumberRequests?.viewAll;
    const [rows, setRows] = useState<ConsumerNumberRecord[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);
    const [retryingMsisdn, setRetryingMsisdn] = useState("");
    const [retryingAll, setRetryingAll] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [merchantInfo, setMerchantInfo] = useState<{ name: string; logo: string } | null>(null);

    useEffect(() => {
        const fetchRecords = async () => {
            if (!token || !jobId) return;

            setLoading(true);
            try {
                const response = await axios.get<ConsumerNumberRecordsResponse>(
                    `${WALEE_BASE_URL}/consumer-number-requests/${jobId}`,
                    {
                        headers: { "x-auth-token": token },
                        params: { page: page + 1, perPage: pageSize, search: search || undefined, status: status || undefined },
                    }
                );
                const result = response.data;
                const records = Array.isArray(result.data) ? result.data : [];

                setRows(
                    records?.map((item, index): ConsumerNumberRecord => ({
                        _id: item._id || item.consumerNumber || `${item.serviceId}-${item.msisdn}-${index}`,
                        serviceId: item.serviceId || "",
                        serviceName: item.serviceName || "",
                        consumerNumber: item.consumerNumber || "",
                        msisdn: item.msisdn || "",
                        fullName: item.fullName || "",
                        customerEmail: item.customerEmail || "",
                        amount: Number(item.amount || 0),
                        dueDate: item.dueDate || "",
                        product: item.product || "",
                        status: item.status || "",
                        createdAt: item.createdAt || "",
                    }))
                );
                setRowCount(result.total || 0);
            } catch (error) {
                console.error("Failed to fetch consumer number request records", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecords();
    }, [token, jobId, page, pageSize, search, status, refreshKey]);

    useEffect(() => {
        if (!rows.length || merchantInfo) return;
        const serviceId = rows[0]?.serviceId;
        if (!serviceId) return;
        axios
            .get(`${WALEE_BASE_URL}/services/get/sid/${serviceId}`)
            .then((res) => {
                const merchant = res.data?.merchant;
                if (merchant) setMerchantInfo({ name: merchant.name || "", logo: merchant.logo || "" });
            })
            .catch(() => { });
    }, [rows]);

    const downloadVoucher = async (row: ConsumerNumberRecord) => {
        const dueDate = row.dueDate
            ? `${row.dueDate.slice(0, 4)}-${row.dueDate.slice(4, 6)}-${row.dueDate.slice(6, 8)}`
            : "-";
        const amount = `PKR ${Number(row.amount || 0).toLocaleString()}`;
        const issueDate = new Date().toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" });
        const voucherRef = `VCH-${row.consumerNumber?.slice(-6) || Date.now().toString().slice(-6)}`;
        const institutionName = merchantInfo?.name || row.serviceName || "Institution";

        const wrapper = document.createElement("div");
        wrapper.style.cssText = "position:fixed;left:-9999px;top:0;z-index:-1;";

        wrapper.innerHTML = `
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>
            <div id="wfs-voucher" style="
                width:520px;
                background:#ffffff;
                font-family:'Poppins',sans-serif;
                font-size:11px;
                border:1px solid #E0E0E0;
            ">
                <!-- Header -->
                <div style="background:linear-gradient(135deg,#6631F7 0%,#9B6BFF 100%);padding:16px 20px;display:flex;align-items:center;gap:14px;">
                    ${merchantInfo?.logo
                        ? `<img src="${merchantInfo.logo}" style="width:46px;height:46px;border-radius:6px;object-fit:cover;background:#fff;border:2px solid rgba(255,255,255,0.3);" />`
                        : `<div style="width:46px;height:46px;border-radius:6px;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;">${institutionName.charAt(0).toUpperCase()}</div>`
                    }
                    <div style="flex:1;">
                        <div style="color:#fff;font-size:13px;font-weight:700;letter-spacing:0.2px;">${institutionName}</div>
                        <div style="color:rgba(255,255,255,0.75);font-size:8px;font-weight:400;margin-top:2px;letter-spacing:1.4px;text-transform:uppercase;">Fee Payment Voucher</div>
                    </div>
                    <div style="text-align:right;">
                        <div style="color:rgba(255,255,255,0.6);font-size:7px;letter-spacing:0.5px;text-transform:uppercase;">Ref No.</div>
                        <div style="color:#fff;font-size:10px;font-weight:600;margin-top:1px;">${voucherRef}</div>
                        <div style="color:rgba(255,255,255,0.6);font-size:7px;margin-top:5px;text-transform:uppercase;">Issue Date</div>
                        <div style="color:#fff;font-size:9px;font-weight:500;margin-top:1px;">${issueDate}</div>
                    </div>
                </div>

                <!-- Student Info Band -->
                <div style="background:#F8F6FF;border-bottom:1px solid #EAE4FF;padding:10px 20px;display:flex;justify-content:space-evenly;flex-wrap:wrap;">
                    <div style="text-align:center;">
                        <div style="font-size:7px;color:#9B8DC4;font-weight:600;letter-spacing:0.8px;text-transform:uppercase;">Student Name</div>
                        <div style="font-size:11px;color:#1A1A2E;font-weight:600;margin-top:2px;">${row.fullName || "-"}</div>
                    </div>
                    <div style="text-align:center;">
                        <div style="font-size:7px;color:#9B8DC4;font-weight:600;letter-spacing:0.8px;text-transform:uppercase;">Mobile</div>
                        <div style="font-size:11px;color:#1A1A2E;font-weight:600;margin-top:2px;">${row.msisdn || "-"}</div>
                    </div>
                    ${row.customerEmail ? `
                    <div style="text-align:center;">
                        <div style="font-size:7px;color:#9B8DC4;font-weight:600;letter-spacing:0.8px;text-transform:uppercase;">Email</div>
                        <div style="font-size:11px;color:#1A1A2E;font-weight:600;margin-top:2px;">${row.customerEmail}</div>
                    </div>` : ""}
                </div>

                <!-- Fee Details Table -->
                <div style="padding:14px 20px 8px;">
                    <div style="font-size:7px;color:#999;font-weight:600;letter-spacing:1px;text-transform:uppercase;margin-bottom:8px;">Fee Details</div>
                    <div style="border:1px solid #EBEBEB;border-radius:5px;overflow:hidden;">
                        <div style="display:flex;justify-content:space-between;background:#FAFAFA;padding:7px 12px;border-bottom:1px solid #EBEBEB;">
                            <span style="font-size:8px;color:#888;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Description</span>
                            <span style="font-size:8px;color:#888;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Amount</span>
                        </div>
                        <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border-bottom:1px solid #EBEBEB;">
                            <span style="font-size:10px;color:#333;font-weight:500;">${row.product || row.serviceName || "Fee"}</span>
                            <span style="font-size:10px;color:#333;font-weight:600;">${amount}</span>
                        </div>
                        <div style="display:flex;justify-content:space-between;align-items:center;padding:9px 12px;background:#6631F7;">
                            <span style="font-size:9px;color:rgba(255,255,255,0.9);font-weight:600;letter-spacing:0.3px;">Total Payable</span>
                            <span style="font-size:12px;color:#fff;font-weight:700;">${amount}</span>
                        </div>
                    </div>
                </div>

                <!-- Due Date Warning -->
                <div style="margin:0 20px 14px;background:#FFF8E7;border:1px solid #FFE4A0;border-radius:5px;padding:8px 12px;display:flex;justify-content:space-between;align-items:center;">
                    <div style="font-size:8px;color:#B8860B;font-weight:500;">Payment Due Date</div>
                    <div style="font-size:11px;color:#8B6400;font-weight:700;">${dueDate}</div>
                </div>

                <!-- Dashed Separator -->
                <div style="margin:0 20px;border-top:1.5px dashed #CCC;position:relative;display:flex;align-items:center;justify-content:center;">
                    <span style="position:absolute;background:#fff;padding:0 8px;font-size:8px;color:#BBB;letter-spacing:0.5px;">&#9998; tear here</span>
                </div>

                <!-- Consumer Number Section -->
                <div style="padding:14px 20px 10px;">
                    <div style="font-size:7px;color:#9B8DC4;font-weight:600;letter-spacing:1.2px;text-transform:uppercase;text-align:center;margin-bottom:8px;">Consumer Number for Payment</div>
                    <div style="border:2px solid #6631F7;border-radius:6px;padding:12px;text-align:center;background:#F8F6FF;">
                        <div style="font-size:22px;font-weight:700;color:#6631F7;letter-spacing:5px;">${row.consumerNumber || "-"}</div>
                    </div>
                </div>

                <!-- Payment Steps -->
                <div style="background:#F8F6FF;border-top:1px solid #EAE4FF;padding:12px 20px;">
                    <div style="font-size:7px;color:#8B78C4;font-weight:600;letter-spacing:1px;text-transform:uppercase;margin-bottom:9px;">How to Pay</div>
                    <div style="display:flex;gap:0;align-items:flex-start;">
                        ${[
                            ["1", "Open JazzCash or Easypaisa app on your phone"],
                            ["2", "Go to Bill Payment and select <b>Hakeem</b> as biller"],
                            ["3", "Enter the Consumer Number above and confirm payment"],
                        ].map(([num, text]) => `
                            <div style="display:flex;flex-direction:column;align-items:center;flex:1;text-align:center;padding:0 5px;">
                                <div style="width:18px;height:18px;border-radius:50%;background:#6631F7;color:#fff;font-size:8px;font-weight:700;display:flex;align-items:center;justify-content:center;margin-bottom:5px;">${num}</div>
                                <div style="font-size:8px;color:#555;font-weight:400;line-height:1.45;">${text}</div>
                            </div>
                        `).join('<div style="width:1px;background:#DDD;margin-top:9px;height:26px;flex-shrink:0;"></div>')}
                    </div>
                </div>

                <!-- Footer -->
                <div style="background:#1A1A2E;padding:8px 20px;display:flex;justify-content:space-between;align-items:center;">
                    <div style="font-size:7.5px;color:rgba(255,255,255,0.35);">This is a system generated voucher. No signature required.</div>
                    <div style="font-size:7.5px;color:rgba(255,255,255,0.25);">Powered by WFS</div>
                </div>
            </div>
        `;

        document.body.appendChild(wrapper);
        await new Promise((r) => setTimeout(r, 400));

        const el = wrapper.querySelector("#wfs-voucher") as HTMLElement;
        const canvas = await html2canvas(el, { scale: 3, useCORS: true, backgroundColor: "#ffffff" });

        document.body.removeChild(wrapper);

        const imgData = canvas.toDataURL("image/png");
        const doc = new jsPDF({ unit: "px", format: [canvas.width / 3, canvas.height / 3] });
        doc.addImage(imgData, "PNG", 0, 0, canvas.width / 3, canvas.height / 3);
        doc.save(`voucher-${row.consumerNumber || row.msisdn}.pdf`);
    };

    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPage(0);
        setSearch(searchInput.trim());
    };

    const retryRecord = async (msisdn: string) => {
        if (!token || !jobId || !msisdn || retryingMsisdn || retryingAll) return;

        const confirmed = await confirmAlert(
            "Retry this request?",
            `Consumer number request for ${msisdn} will be sent again to all gateways.`,
            "Yes, retry"
        );
        if (!confirmed) return;

        const headers = {
            "x-auth-token": token,
            "Authorization": process.env.REACT_APP_ADMIN_PRODUCTS
        };

        setRetryingMsisdn(msisdn);
        try {
            await axios.post(
                `${WALEE_BASE_URL}/consumer-number-requests/retry`,
                { jobId, msisdn },
                { headers }
            );
            toast.success("Consumer number request queued for retry.", { position: "top-center" });
            setRefreshKey((current) => current + 1);
        } catch (error) {
            const message = (error as any)?.response?.data?.message;
            toast.error(message || "Failed to retry consumer number request.", { position: "top-center" });
        } finally {
            setRetryingMsisdn("");
        }
    };

    const retryAllFailed = async () => {
        if (!token || !jobId || retryingMsisdn || retryingAll) return;

        const confirmed = await confirmAlert(
            "Retry all failed requests?",
            "All failed consumer number requests in this job will be sent again to all gateways.",
            "Yes, retry all"
        );
        if (!confirmed) return;

        const headers = {
            "x-auth-token": token,
            "Authorization": process.env.REACT_APP_ADMIN_PRODUCTS
        };

        setRetryingAll(true);
        try {
            await axios.post(
                `${WALEE_BASE_URL}/consumer-number-requests/retry`,
                { jobId },
                { headers }
            );
            toast.success("Failed consumer number requests queued for retry.", { position: "top-center" });
            setRefreshKey((current) => current + 1);
        } catch (error) {
            const message = (error as any)?.response?.data?.message;
            toast.error(message || "Failed to retry failed consumer number requests.", { position: "top-center" });
        } finally {
            setRetryingAll(false);
        }
    };

    const exportData = async () => {
        if (!token || !jobId || exporting) return;

        setExporting(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_CSV_JOB_URL}/csv-jobs/consumer-number-requests/`,
                { jobId, search, status },
                { headers: { "x-auth-token": token } }
            );
            if (response.data.is_success) {
                successAlert("Success", "CSV file is being generated. You will be notified once it is ready.");
            } else {
                infoAlert("Failed", "Failed to generate CSV file");
            }
        } catch (error) {
            const message = (error as any)?.response?.data?.message;
            infoAlert("Failed", message || "Failed to generate CSV file");
        } finally {
            setExporting(false);
        }
    };

    const columns: GridColDef[] = [
        { field: "serviceId", headerName: "Service ID", width: 140 },
        { field: "serviceName", headerName: "Service Name", width: 170 },
        { field: "consumerNumber", headerName: "Consumer Number", width: 190, renderCell: ({ value }) => value || "-" },
        { field: "msisdn", headerName: "Mobile", width: 160 },
        { field: "fullName", headerName: "Customer Name", width: 180 },
        { field: "customerEmail", headerName: "Email", width: 200, renderCell: ({ value }) => value || "-" },
        {
            field: "amount",
            headerName: "Amount",
            width: 120,
            valueFormatter: ({ value }) => `PKR ${Number(value || 0).toLocaleString()}`,
        },
        { field: "dueDate", headerName: "Due Date", width: 140 },
        { field: "product", headerName: "Product", width: 150 },
        {
            field: "status",
            headerName: "Status",
            width: 130,
            renderCell: ({ value }) => {
                const status = String(value || "");
                const style = statusStyles[status.toLowerCase()] || { background: "#F1F1F1", color: "#3C3C3C" };

                return (
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 10,
                            backgroundColor: style.background,
                            color: style.color,
                            fontSize: "12px",
                            fontWeight: 600,
                        }}
                    >
                        {status || "-"}
                    </Box>
                );
            },
        },
        {
            field: "createdAt",
            headerName: "Created At",
            width: 180,
            valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : "-"),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => {
                if (row.status?.toLowerCase() === "failed") {
                    return (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                            disabled={!!retryingMsisdn || retryingAll}
                            onClick={() => retryRecord(row.msisdn)}
                            sx={{ textTransform: "none", borderColor: colorPalette.primary, color: colorPalette.primary }}
                        >
                            {retryingMsisdn === row.msisdn ? "Retrying..." : "Retry"}
                        </Button>
                    );
                }
                if (row.status?.toLowerCase() === "success") {
                    return (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                            onClick={() => downloadVoucher(row)}
                            sx={{
                                textTransform: "none",
                                backgroundColor: colorPalette.primary,
                                boxShadow: "none",
                                "&:hover": { backgroundColor: colorPalette.primary, boxShadow: "none" },
                            }}
                        >
                            Voucher
                        </Button>
                    );
                }
                return null;
            },
        },
    ];

    if (!canView) {
        return (
            <>
                <PageHeader title="Consumer Number Request Records" />
                <Box sx={{ my: 2 }}>
                    <Card>
                        <CardContent>You do not have permission to view this page.</CardContent>
                    </Card>
                </Box>
            </>
        );
    }

    return (
        <>
            <Button onClick={() => history.push("/consumer-number-requests")} sx={{ mb: 2, textTransform: "none" }}>
                Back to jobs
            </Button>
            <PageHeader title="Consumer Number Request Records" />
            <Box
                sx={{
                    my: 2,
                    borderTop: `4px solid ${colorPalette.primary}`,
                    borderTopLeftRadius: "4px",
                    borderTopRightRadius: "4px",
                }}
            >
                <Card>
                    <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, width: "100%" }}>
                            <Box
                                component="form"
                                onSubmit={handleSearch}
                                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, maxWidth: 800 }}
                            >
                                <TextField
                                    sx={{ flex: 1, minWidth: 220 }}
                                    size="small"
                                    value={searchInput}
                                    onChange={(event) => setSearchInput(event.target.value)}
                                    placeholder="Search by mobile, email, name, amount or consumer no"
                                    inputProps={{ "aria-label": "Search by mobile, email, name, amount or consumer no" }}
                                />
                                <Box sx={{ width: 165, flexShrink: 0 }}>
                                    <SelectComponent
                                        value={status}
                                        onSelect={(event) => {
                                            setStatus(event.target.value);
                                            setPage(0);
                                        }}
                                        menuItems={[
                                            { label: "All", value: "" },
                                            { label: "Success", value: "Success" },
                                            { label: "Failed", value: "Failed" },
                                        ]}
                                        placeHolder="Status"
                                        size="small"
                                    />
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SearchIcon />}
                                    sx={{
                                        minWidth: 112,
                                        borderRadius: "4px",
                                        backgroundColor: colorPalette.primary,
                                        boxShadow: "none",
                                        textTransform: "none",
                                        whiteSpace: "nowrap",
                                        "&:hover": { backgroundColor: colorPalette.primary, boxShadow: "none" },
                                    }}
                                >
                                    Search
                                </Button>
                            </Box>
                            <Button
                                variant="outlined"
                                disabled={exporting}
                                onClick={exportData}
                                sx={{
                                    ml: "auto",
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                    textTransform: "none",
                                    borderColor: colorPalette.primary,
                                    color: colorPalette.primary,
                                }}
                            >
                                {exporting ? "Exporting..." : "Export"}
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={retryingAll || !!retryingMsisdn}
                                onClick={retryAllFailed}
                                sx={{
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                    textTransform: "none",
                                    borderColor: colorPalette.primary,
                                    color: colorPalette.primary,
                                }}
                            >
                                {retryingAll ? "Retrying..." : "Retry All Failed"}
                            </Button>
                        </Box>
                        <Box sx={{ height: 550, width: "100%", overflow: "auto" }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                getRowId={(row) => row._id}
                                disableSelectionOnClick
                                autoHeight
                                paginationMode="server"
                                page={page}
                                pageSize={pageSize}
                                rowsPerPageOptions={[25, 50, 100]}
                                rowCount={rowCount}
                                loading={loading}
                                onPageChange={setPage}
                                onPageSizeChange={(newPageSize) => {
                                    setPageSize(newPageSize);
                                    setPage(0);
                                }}
                                sx={{
                                    border: "none",
                                    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F7F7F7" },
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({ token: state.login.token });
const connector = connect(mapStateToProps);
const ConsumerNumberRequestRecordsComponentRedux = connector(ConsumerNumberRequestRecordsComponent);

export { ConsumerNumberRequestRecordsComponentRedux as ConsumerNumberRequestRecordsComponent };
